import { Box, useTheme, useMediaQuery } from "@mui/material";
import { useEffect, useState, useRef, useMemo } from "react";
import type { CoinPosition, PegPosition } from "../types/schema";

const median = (arr: number[]) =>
  arr.length ? [...arr].sort((a, b) => a - b)[Math.floor(arr.length / 2)] : 0;

function estimateSpacing(pegs: PegPosition[]) {
  if (!pegs.length) return { s: 0 };
  const ROW_TOL = 6;
  const ys = [...new Set(pegs.map((p) => p.y))]
    .sort((a, b) => a - b)
    .reduce((acc, y) => {
      const last = acc[acc.length - 1];
      if (!last || Math.abs(last[last.length - 1] - y) > ROW_TOL) acc.push([y]);
      else last.push(y);
      return acc;
    }, [] as number[][])
    .map((group) => median(group));

  const sSamples: number[] = [];
  ys.forEach((yv) => {
    const row = pegs
      .filter((p) => Math.abs(p.y - yv) <= ROW_TOL)
      .map((p) => p.x)
      .sort((a, b) => a - b);
    for (let i = 1; i < row.length; i++) sSamples.push(row[i] - row[i - 1]);
  });

  const s = median(sSamples);
  return { s };
}

interface DroppingCoinProps {
  startX: number;
  startY: number;
  pegs: PegPosition[];
  onLanded: (slotIndex: number) => void;
  slotWidth: number;
  slotCount: number;
  boardWidth: number;
  targetSlot?: number;
}

export const DroppingCoin = ({
  startX,
  startY,
  pegs,
  onLanded,
  slotWidth,
  slotCount,
  boardWidth,
  targetSlot,
}: DroppingCoinProps) => {
  const [position, setPosition] = useState<CoinPosition>({
    x: startX,
    y: startY,
  });
  const velocityRef = useRef({ x: 0, y: 0 });
  const animationRef = useRef<number | undefined>(undefined);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const stillFramesRef = useRef(0);
  const lastPosRef = useRef({ x: startX, y: startY });

  // 📐 spacing & kolizioni radijus
  const { s } = useMemo(() => estimateSpacing(pegs), [pegs]);
  const visualCoinRadius = isMobile ? 10 : 14;
  const pegRadius = isMobile ? 8 : 10;
  const margin = 1.5;
  let collisionCoinRadius = Math.max(
    2,
    (Math.sqrt(3) / 6) * s - pegRadius - margin
  );
  collisionCoinRadius = Math.min(collisionCoinRadius, visualCoinRadius - 1);

  useEffect(() => {
    // 🌍 Fizika
    const gravityBase = isMobile ? 2.2 : 1.6;
    const gravityMax = isMobile ? 3.3 : 2.5;
    const gravityRampTime = 35;
    const wallBounce = 0.52;
    const pegBounce = 0.42; // manji bounce na pegovima = mekši sudar
    const friction = 0.996;
    const maxVelocity = isMobile ? 15 : 13;
    const landingY = isMobile ? 400 : 580;

    // 🐢 Usporavanje
    const fallSpeedFactor = 0.2;

    // 🎯 Guidance ramp postavke
    const guidanceStartY = targetSlot === 4 ? -50 : -400;

    const guidanceFullY = 400;

    let frameCount = 0;

    if (isMobile) {
      setPosition((prev) => ({ ...prev, y: prev.y + 25 }));
    }

    // 📍 Bez početnog random X → ravno pada
    velocityRef.current.y = 1.5;

    if (targetSlot === 4) {
      velocityRef.current.x = -3; // samo blago
    } else {
      velocityRef.current.x = 0;
    }
    let lastFrameTime = performance.now();

    const animate = () => {
      frameCount++;
      const now = performance.now();
      const delta = (now - lastFrameTime) / (1000 / 60); // 1 = normalna brzina pri 60fps
      lastFrameTime = now;

      const gravity =
        (gravityBase +
          Math.min(frameCount / gravityRampTime, 1) *
            (gravityMax - gravityBase)) *
        fallSpeedFactor;

      setPosition((prev) => {
        let newX = prev.x + velocityRef.current.x * delta;
        let newY = prev.y + velocityRef.current.y * delta;

        velocityRef.current.y += gravity * delta;

        velocityRef.current.x *= 1 - (1 - friction) * fallSpeedFactor;
        velocityRef.current.y *= 1 - (1 - 0.998) * fallSpeedFactor;

        // 🚫 max brzina
        const speed = Math.sqrt(
          velocityRef.current.x ** 2 + velocityRef.current.y ** 2
        );
        if (speed > maxVelocity * fallSpeedFactor * 1.5) {
          const scale = (maxVelocity * fallSpeedFactor * 1.5) / speed;
          velocityRef.current.x *= scale;
          velocityRef.current.y *= scale;
        }

        // 🪵 Kolizije s pegovima (soft bounce)
        pegs.forEach((peg) => {
          const dx = newX - peg.x;
          const dy = newY - peg.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < collisionCoinRadius + pegRadius) {
            const angle = Math.atan2(dy, dx);
            const targetX =
              peg.x + Math.cos(angle) * (collisionCoinRadius + pegRadius);
            const targetY =
              peg.y + Math.sin(angle) * (collisionCoinRadius + pegRadius);

            const normalX = dx / distance;
            const normalY = dy / distance;
            const dotProduct =
              velocityRef.current.x * normalX + velocityRef.current.y * normalY;

            velocityRef.current.x =
              (velocityRef.current.x - 2 * dotProduct * normalX) * pegBounce;
            velocityRef.current.y =
              (velocityRef.current.y - 2 * dotProduct * normalY) * pegBounce;

            // Mali “kick” da ne zapne
            velocityRef.current.x += (Math.random() - 0.5) * 0.15;

            newX = targetX;
            newY = targetY;
          }
        });

        // 🧲 Guidance — postepeno raste (ramp)
        // 🧲 Guidance — postepeno raste (ramp)
        if (targetSlot !== undefined && newY > guidanceStartY) {
          const slotW = boardWidth / slotCount;
          const centerX = boardWidth / 2;
          const slotOffset = targetSlot - Math.floor(slotCount / 2);
          const targetX = centerX + slotOffset * slotW;

          const distanceToTarget = targetX - newX;
          const rampProgress = Math.min(
            (newY - guidanceStartY) / (guidanceFullY - guidanceStartY),
            1
          );

          let guidanceMultiplier = 0.002 * (1 / fallSpeedFactor);
          if (targetSlot === 4) guidanceMultiplier *= 80;

          if (rampProgress < 0.4) {
            guidanceMultiplier *= 2.2; // jači početni “magnet”
          } else if (rampProgress > 0.9) {
            guidanceMultiplier *= 0.6; // smanji kako se približava
          }
          // 📈 dodatno pojačanje blizu dna
          const boostNearBottom =
            newY > guidanceFullY - 150
              ? 1 + (newY - (guidanceFullY - 150)) * 0.035
              : 1;

          velocityRef.current.x +=
            distanceToTarget *
            guidanceMultiplier *
            0.2 *
            rampProgress ** 1.4 *
            boostNearBottom;
          console.log(
            `[GUIDE] y=${newY.toFixed(1)} x=${newX.toFixed(
              1
            )} | dist=${distanceToTarget.toFixed(
              2
            )} | ramp=${rampProgress.toFixed(
              2
            )} | mult=${guidanceMultiplier.toFixed(
              4
            )} | velX=${velocityRef.current.x.toFixed(2)}`
          );
        }

        // ⬅➡ Zidovi
        const leftBuffer = -20; // koliko može ući “iza zida”
        if (newX < leftBuffer) {
          newX = leftBuffer;
          velocityRef.current.x *= -wallBounce * 0.5; // manji bounce, da ne odbija prejako
        }
        if (newX > boardWidth - visualCoinRadius) {
          newX = boardWidth - visualCoinRadius;
          velocityRef.current.x *= -wallBounce;
        }

        // 🧊 Anti-stick — blagi pomak ako se zaglavi
        if (
          Math.abs(newX - lastPosRef.current.x) < 0.25 &&
          Math.abs(newY - lastPosRef.current.y) < 0.25
        ) {
          stillFramesRef.current++;
          if (stillFramesRef.current > 12) {
            velocityRef.current.x += (Math.random() - 0.5) * 1.2;
            velocityRef.current.y += 1;
            stillFramesRef.current = 0;
          }
        } else {
          stillFramesRef.current = 0;
          lastPosRef.current = { x: newX, y: newY };
        }

        // 🏁 Dno
        if (newY > landingY) {
          let slotIndex = Math.floor((newX / boardWidth) * slotCount);
          if (targetSlot !== undefined) slotIndex = targetSlot;
          const clampedIndex = Math.max(0, Math.min(slotCount - 1, slotIndex));

          console.log(
            `[LAND] newX=${newX.toFixed(1)} landingY=${newY.toFixed(
              1
            )} calculatedSlot=${slotIndex} clamped=${clampedIndex}`
          );

          onLanded(clampedIndex);
          return prev;
        }

        return { x: newX, y: newY };
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [
    pegs,
    onLanded,
    slotWidth,
    slotCount,
    boardWidth,
    isMobile,
    targetSlot,
    collisionCoinRadius,
  ]);

  return (
    <Box
      component="img"
      src="/coin-gold.png"
      sx={{
        position: "absolute",
        left: position.x - visualCoinRadius,
        top: position.y - (isMobile ? 70 : 100),
        width: visualCoinRadius * 2,
        height: visualCoinRadius * 2,
        pointerEvents: "none",
        zIndex: 10,
        transition: "transform 0.05s linear",
        transform: `rotate(${position.x * 1.5}deg)`,
      }}
    />
  );
};
